import type {
  RecordsRequestStatusResponse,
  SubmissionAttemptStatus,
} from '@/services/api/agentflowService';

export type RecordsRequestAgentDisplayState =
  | 'running'
  | 'ready'
  | 'blocked'
  | 'failed';

const STEP_LABELS: Record<number, string> = {
  0: 'Researching destination...',
  1: 'Resolving signature...',
  2: 'Selecting documents...',
  3: 'Filling in patient details...',
  4: 'Assembling the package...',
  5: 'Reviewing submission...',
};

export const RECORDS_REQUEST_AGENT_TERMINAL_STATUSES = new Set([
  'completed',
  'ready',
  'blocked',
  'failed',
  'needs_rebuild',
  'needs_re_evaluation',
  'submitting',
  'submitted',
]);

export function getRecordsRequestAgentDisplayState(
  data: RecordsRequestStatusResponse,
): RecordsRequestAgentDisplayState | null {
  const { status } = data;

  if (status === 'running' || status === 'draft') return 'running';
  if (
    status === 'ready' ||
    status === 'submitting' ||
    status === 'submitted'
  ) {
    return 'ready';
  }
  if (
    status === 'blocked' ||
    status === 'needs_rebuild' ||
    status === 'needs_re_evaluation'
  ) {
    return 'blocked';
  }
  if (status === 'failed') return 'failed';
  if (status === 'completed') {
    return data.ready_to_submit ? 'ready' : 'blocked';
  }

  return null;
}

export function isNewRecordsRequestBackendShape(
  data: RecordsRequestStatusResponse,
): boolean {
  return (
    data.status === 'ready' ||
    data.status === 'blocked' ||
    data.status === 'draft' ||
    data.status === 'needs_rebuild' ||
    data.status === 'needs_re_evaluation' ||
    data.status === 'submitting' ||
    data.status === 'submitted' ||
    data.blocker_count != null
  );
}

export function getRecordsRequestStepLabel(
  data?: RecordsRequestStatusResponse,
): string | null {
  if (!data) return null;

  if (data.step_label) {
    return data.step_label.endsWith('...')
      ? data.step_label
      : `${data.step_label}...`;
  }

  if (data.step_index != null) {
    return STEP_LABELS[data.step_index] ?? null;
  }

  return null;
}

export function getRecordsRequestButtonLabel(
  displayState: Exclude<RecordsRequestAgentDisplayState, 'running'>,
  blockerCount: number,
): string {
  if (displayState === 'ready') {
    return 'Review Submission Attempt';
  }

  if (displayState === 'blocked') {
    if (blockerCount === 1) {
      return '1 Item Needs Input';
    }
    if (blockerCount > 1) {
      return `${blockerCount} Items Need Input`;
    }
    return 'Needs Input';
  }

  return 'View Submission Attempt';
}

export function getSubmissionAttemptDisplayState(
  status?: SubmissionAttemptStatus | null,
): RecordsRequestAgentDisplayState | null {
  if (!status) return null;

  if (status === 'draft') return 'running';
  if (
    status === 'ready' ||
    status === 'submitting' ||
    status === 'submitted'
  ) {
    return 'ready';
  }
  if (
    status === 'blocked' ||
    status === 'needs_rebuild' ||
    status === 'needs_re_evaluation'
  ) {
    return 'blocked';
  }
  if (status === 'failed') {
    return 'failed';
  }

  return null;
}
